import { useEffect, useRef } from 'react';
import {
  Node as ReactFlowNode,
  useStore,
  useStoreApi,
  useUpdateNodeInternals,
} from 'reactflow';
import { useLineageGraphContext } from '../LineageGraphContext';
import {
  LineageNodeDegrees,
  LineageZoomTier,
  ReactFlowNodeData,
} from '../types';
import { selectLineageZoomTier } from '../utils/lineageGraphUtils';

type MeasuredState = {
  zoomTier?: LineageZoomTier;
  nodeDegrees?: LineageNodeDegrees;
  nodeData: ReactFlowNodeData[];
};

// Compact nodes keep a fixed-size footprint, so React Flow's resize observer
// never notices their handles moving when the pill/dot inside changes (tier,
// dot size, expand arrow, expanded width) or when they turn back into cards.
// Re-measure every node in one batch whenever any of those inputs change;
// position-only changes (drag) are skipped.
const useSyncCompactNodeInternals = (
  graphNodes: ReactFlowNode<ReactFlowNodeData>[],
) => {
  const zoomTier = useStore(selectLineageZoomTier);
  const { isControlled, nodeDegrees } = useLineageGraphContext();
  const store = useStoreApi();
  const updateNodeInternals = useUpdateNodeInternals();
  const measuredRef = useRef<MeasuredState>({ nodeData: [] });

  useEffect(() => {
    const updateGraphNodeInternals = () =>
      updateNodeInternals(
        store
          .getState()
          .getNodes()
          .filter((node) => node.type === 'graphNode')
          .map((node) => node.id),
      );

    if (isControlled || zoomTier === 'card') {
      const wasCompact = !!measuredRef.current.zoomTier;
      measuredRef.current = { nodeData: [] };
      if (wasCompact && !isControlled) updateGraphNodeInternals();
      return;
    }

    const nodeData = graphNodes.map((node) => node.data);
    const measured = measuredRef.current;
    const isUnchanged =
      measured.zoomTier === zoomTier &&
      measured.nodeDegrees === nodeDegrees &&
      measured.nodeData.length === nodeData.length &&
      nodeData.every((data, i) => data === measured.nodeData[i]);
    measuredRef.current = { zoomTier, nodeDegrees, nodeData };
    if (isUnchanged) return;

    updateGraphNodeInternals();
  }, [
    graphNodes,
    isControlled,
    zoomTier,
    nodeDegrees,
    store,
    updateNodeInternals,
  ]);
};

export default useSyncCompactNodeInternals;
